import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import React, {useEffect, useMemo, useRef, useState} from "react";
import Button from "../../../components/button/button.jsx";
import {FaMapMarker, FaPrint, FaSearch} from "react-icons/fa";
import {useMutation, useQuery} from "@tanstack/react-query";
import userService from "../../../services/web/user-service.js";
import projectUtils from "../../../utils/project-utils.js";
import cortexService from "../../../services/web/cortex-service.js";
import {AgGridReact} from "ag-grid-react";
import AgGridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import PrintTemplate from "../../../components/reports/print-reports/print-template.jsx";
import HeaderTemplateButton from "../../../components/button/header-template-button.jsx";
import PrintableTableAgGrid from "../../../components/printable-table/printable-table-ag-grid.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {MaskDefault} from "../../../utils/mask.js";
import ReportUtils from "../../../utils/report-utils.js";
import {useSearchParams} from "react-router";

const CortexSearch = () => {
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [gridReady, setGridReady] = useState(false);
  const [plate, setPlate] = useState("");
  const [data, setData] = useState([]);
  const [entity, setEntity] = useState(-1);
  const printComponentRef = useRef(null);
  const gridApi = useRef(null);

  const columnDefs = useMemo(() => [
    {headerName: "D/H da passagem", field: "dataPassagem", flex: 2},
    {headerName: "Local", field: "local", flex: 6},
    {headerName: "Localização", field: "customLocation", flex: 2, sortable: false, printableTableAlias: "Coordenadas",
      cellRenderer: ({ data }) => {
        if (!data?.latitude || !data?.longitude) return <span>Sem coordenadas</span>;

        const url = `https://www.google.com/maps?q=${data.latitude},${data.longitude}`;

        return (
          <a
            title="Abrir no Google Maps"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-start gap-1 text-[#2e3742]!"
          >
            <FaMapMarker size={18}/>
            Mapa
          </a>
        );
      }
    }
  ], []);

  const {data: currentUser = "", isSuccess} = useQuery({
    queryFn: userService.getUserLogged,
    queryKey: ['currentUser'],
    select: data => data.cpf
  })

  useEffect(() => {
    if (!isSuccess) return;

    const plateParams = searchParams.get("plate");

    if (plateParams) {
      setPlate(plateParams);
      search(plateParams);
    }
  }, [isSuccess, searchParams]);

  const mutation = useMutation({
    mutationFn: (searchPlate) => cortexService.search({ plate: searchPlate }),
    onSuccess: data => setData(data),
    onError: err => {
      console.error(err);
      let message = 'Não foi possível realizar a busca.';

      if (err.response?.status !== 500) {
        switch (err.response?.data?.message) {
          case "cortex.not.enabled": message = "Cortex desativado."; break;
          case "cortex.cpf.invalid": message = "O usuário atual não possui CPF cadastrado."; break;
          case "cortex.token.expired": message = "Chave do cortex expirada."; break;
          case "cortex.token.invalid": message = "Chave do cortex inválida."; break;
        }
      }

      projectUtils.swalErrorMessage("", message)
    }
  });

  const search = async (plateParams = undefined) => {
    const plate_ = plateParams ?? plate

    if (!plate_) {
      projectUtils.swalErrorMessage('', "O campo \"Placa\" é obrigatório.", 'Ok');
      return;
    }

    if (plate_.length < 7) {
      projectUtils.swalErrorMessage('', "Informe a placa completa.", 'Ok');
      return;
    }

    mutation.reset();
    setGridReady(false);

    await mutation.mutateAsync(plate_);
  };

  const printableTableFormatters = {
    customLocation: row => {
      return row.latitude + ', ' + row.longitude
    }
  }

  useGlobalKey("Enter", search);

  const filterLabels = {
    "plate": "Placa"
  }

  useEffect(() => {
    if (data.length === 0) return;
    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...{plate: plate},
      }, filterLabels)
    );

  }, [data]);

  return(
    <Template isLoading={isLoading || mutation.isPending}>
      <Template.Header>
        <Template.Title>
          Pesquisa de passagens - Córtex
        </Template.Title>
        <div className="flex gap-2">
          <Button title="Imprimir" onClick={() => {
            if(!gridApi.current) return
            const data = [];

            gridApi.current.forEachNode((node) => {
              data.push(node.data);
            });

            printComponentRef.current?.print()
          }}><FaPrint/></Button>
          <HeaderTemplateButton selected={entity} setSelected={setEntity}/>
        </div>
      </Template.Header>
      <Template.Body>
        <Card>
          <Card.Body className="grid grid-cols-12 items-center">
            <div className="col-span-3 flex gap-4">
              <div className="flex-1">
                <InputForm
                  id="plate"
                  label="Placa *"
                  value={plate}
                  mask={MaskDefault.plate}
                  onChange={({target}) => setPlate(target.value?.toUpperCase())}
                />
              </div>
              <Button onClick={() => search()} title="Buscar"><FaSearch/></Button>
            </div>
            <div className="col-span-9 flex items-center justify-center text-[12px] font-bold italic text-center">
              {currentUser
                ? <span>
                    * Por exigência do Ministério da Justiça, as consultas efetuadas<br/>
                      durante essa sessão serão vinculadas ao CPF do usuário ativo: <br/>
                      {projectUtils.formatCpfToView(currentUser)}
                  </span>
                : <span className="text-[#ed5565]">* O usuário atual não possui CPF cadastrado.</span>
              }
            </div>
          </Card.Body>
        </Card>
        <Card className="mt-2">
          <Card.Body>
            <div className="h-[calc(100vh-175px)]">
              <PrintTemplate title={"Pesquisa de passagens - córtex"} printHeaderClassname="hidden print:block" printFooterClassname="hidden print:block" entity={entity} ref={printComponentRef} setIsPrinting={setIsLoading}>
                <div id="filters-print" className={'transform !-translate-y-[8.5px]'}></div>
                <div className="print:border-b print:border-gray-200 print:mb-6 print:w-full" />
                {!mutation.isSuccess && !mutation.isPending && (
                  <div id="notAppliedMessage" className={"alert alert-info p-2 h6 text-center"}>
                    Aplique os filtros para exibir o relatório.
                  </div>
                )}
                {mutation.isSuccess && !mutation.isPending && data.length === 0 && (
                  <div id="noDataMessage" className={"alert alert-info p-2 h6 text-center"}>
                    Nenhum registro encontrado com placa informada.
                  </div>
                )}
                {mutation.isSuccess && data.length !== 0 && gridReady && !mutation.isPending &&
                  <PrintableTableAgGrid gridApi={gridApi.current} formatters={printableTableFormatters}
                  />
                }
                {mutation.isSuccess && !mutation.isPending && data.length !== 0 && (
                  <div className="h-[calc(100vh-176px)] print:hidden">
                    <AgGridReact
                      onGridReady={params => {
                        setGridReady(true);
                        gridApi.current = params.api
                      }}
                      rowData={data}
                      columnDefs={columnDefs}
                      getRowId={(params) => params.data.idMovimento}
                      animateRows={false}
                      suppressHorizontalScroll={true}
                      suppressCellFocus={true}
                      suppressHeaderFocus={true}
                      defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                      gridOptions={{ localeText: AgGridUtils.localization() }}
                      theme={AgGridUtils.generalTheme()}
                    />
                  </div>
                )}
              </PrintTemplate>
            </div>
          </Card.Body>
        </Card>
      </Template.Body>
    </Template>
  )
}

export default CortexSearch;